/**
 * @module  public/js/controllers/stepControllerIndex.js
 * @summary Registry / factory – resolves a Focus-Mode **Step Controller**
 *          class from a short key (“call”, “email”, …).
 *
 * @description
 * In Focus-Mode each task type is rendered by a dedicated *step controller*
 * (UI component) that owns its primary/secondary CTA buttons.
 * This registry maps a **key** to the corresponding class so the
 * `FocusModePageController` can do:
 *
 * ```js
 * const StepCtl = di.stepControllerIndex.getController(stepKey);
 * new StepCtl({ container, task, services, onComplete });
 * ```
 *
 * ## Public API
 * – `getController(key = 'call') → class` – returns a constructor; falls
 *   back to `'call'` when the key is unknown.
 *
 * ## Dependencies
 * { FocusModeCallStepController, …future step controllers }
 *  (No external DI; import at module scope)
 *
 * @author   Auto-Generated
 * @version  1.0.0
 * @since    2025-05-02
 */

import FocusModeCallStepController from './focusmodecallstepcontroller.js';

const REGISTRY = {
  call: FocusModeCallStepController,
  call_email: FocusModeCallStepController,
  call_textmessage: FocusModeCallStepController,
  email: FocusModeCallStepController,
  linkedin: FocusModeCallStepController,
  linkedin_activity: FocusModeCallStepController,
  linkedin_message: FocusModeCallStepController,
  sms: FocusModeCallStepController,
  sms_message: FocusModeCallStepController,
  text: FocusModeCallStepController,
  // linkedin: FocusModeLinkedInStepController
};

/**
 * Resolve a step-controller constructor from a registry key.
 * @param   {string} [key='call'] – registry key (“call”, “email”, …)
 * @returns {typeof FocusModeCallStepController} constructor function
 */
export function getController(key = 'call') {
  const normalized = typeof key === 'string' ? key.toLowerCase() : 'call';
  return REGISTRY[normalized] || REGISTRY.call;
}
